import { APPENDIX_TOKENS } from '../appendixTokens';
import {
  Block,
  PlanAlign,
  PlanAppendixBlockData,
  PlanMarker,
  PlanVAlign,
  TableAppendixBlockData,
  itemNumbers,
  planMarkersAll,
} from '../config';

/**
 * Geometry of a location plan's markers, shared by the editor page and
 * DocumentPDF so a pin lands in the same place on both.
 *
 * Everything is computed here in MILLIMETRES of the page and handed to each
 * surface as plain numbers -- the editor writes them into an SVG whose viewBox
 * is in mm, the PDF multiplies by 2.83465. In particular the arrow is rotated
 * HERE, in arithmetic, and emitted as a ready-made polygon: react-pdf accepts
 * `transform` on SVG elements but silently ignores it (SVG children never get
 * a layout box, so the rotation has no origin to apply to), so a rotated
 * marker drawn with a transform is upright in the export and only in the
 * export. See "Markers are pre-rotated points" in DEVELOPMENT.md.
 */

export interface PlanBox {
  /** Offset of the fitted image from the content box's top-left, in mm. */
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
}

/**
 * Fit the plan image into the content area under the title: contain, then
 * placed by the plan's alignment (centred and at the top when unset). A plan
 * with no size yet (nothing uploaded) fills the box, so the empty state is the
 * size the plan will be.
 *
 * Both surfaces call this with the same numbers, so the offsets it returns
 * are the one place the plan's position is decided.
 */
export const fitPlanBox = (
  naturalWidth: number,
  naturalHeight: number,
  boxWidthMm: number,
  boxHeightMm: number,
  align: PlanAlign = 'center',
  valign: PlanVAlign = 'top'
): PlanBox => {
  if (!(naturalWidth > 0) || !(naturalHeight > 0)) {
    return { xMm: 0, yMm: 0, widthMm: boxWidthMm, heightMm: boxHeightMm };
  }
  const scale = Math.min(boxWidthMm / naturalWidth, boxHeightMm / naturalHeight);
  const widthMm = naturalWidth * scale;
  const heightMm = naturalHeight * scale;
  const spareX = boxWidthMm - widthMm;
  const spareY = boxHeightMm - heightMm;
  return {
    xMm: align === 'left' ? 0 : align === 'right' ? spareX : spareX / 2,
    yMm: valign === 'middle' ? spareY / 2 : 0,
    widthMm,
    heightMm,
  };
};

export interface MarkerGeometry {
  /** Centre of the circle, in mm of the plan box. */
  cx: number;
  cy: number;
  r: number;
  /** "x1,y1 x2,y2 x3,y3" -- the arrow, already rotated. */
  arrowPoints: string;
  /** The arrow's tip, where the editor puts its rotate handle. */
  tipX: number;
  tipY: number;
}

const rotate = (x: number, y: number, cx: number, cy: number, deg: number): [number, number] => {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = x - cx;
  const dy = y - cy;
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
};

const fmt = (n: number): string => n.toFixed(3);

/**
 * A marker's geometry in mm of the PLAN BOX (the page's content area under
 * the title), not of the image. The marker's x/y are still fractions of the
 * fitted image -- that is what keeps a pin on the spot it marks when the
 * image is aligned, rotated or trimmed -- but they may run past 0..1, so a pin
 * can sit in the white beside a narrow plan, and the layer both surfaces draw
 * the markers on covers the whole box.
 */
export const markerGeometry = (marker: PlanMarker, box: PlanBox): MarkerGeometry => {
  const r = APPENDIX_TOKENS.markerRadiusMm;
  const cx = box.xMm + box.widthMm * marker.x;
  const cy = box.yMm + box.heightMm * marker.y;
  // Unrotated: an arrowhead sitting on top of the circle, pointing up.
  const base = cy - r + 0.2;
  const tip = cy - r - APPENDIX_TOKENS.arrowLengthMm;
  const half = APPENDIX_TOKENS.arrowHalfWidthMm;
  const points: Array<[number, number]> = [
    [cx, tip],
    [cx - half, base],
    [cx + half, base],
  ].map(([x, y]) => rotate(x, y, cx, cy, marker.rotation || 0));
  const [tipX, tipY] = points[0];
  return {
    cx,
    cy,
    r,
    arrowPoints: points.map(([x, y]) => `${fmt(x)},${fmt(y)}`).join(' '),
    tipX,
    tipY,
  };
};

/**
 * Keep a marker's centre inside the plan box, in image fractions. The box is
 * the page's content area; the image is somewhere inside it, so the allowed
 * range runs from the box's left edge (a negative fraction when the image is
 * inset) to its right edge (past 1).
 */
export const clampMarkerToBox = (
  x: number,
  y: number,
  box: PlanBox,
  boxWidthMm: number,
  boxHeightMm: number
): { x: number; y: number } => {
  const minX = -box.xMm / box.widthMm;
  const maxX = (boxWidthMm - box.xMm) / box.widthMm;
  const minY = -box.yMm / box.heightMm;
  const maxY = (boxHeightMm - box.yMm) / box.heightMm;
  return {
    x: Math.min(maxX, Math.max(minX, x)),
    y: Math.min(maxY, Math.max(minY, y)),
  };
};

/**
 * Whether any location plan numbering its markers from this table has a
 * marker on this row. Such a row may not be turned into a section heading:
 * a heading takes no item number, so its marker would be left on the plan as
 * an empty pin -- and the plan's place menu lists only rows, so the author
 * would have no way to take it off.
 */
export const isTableRowOnAPlan = (blocks: Block[], tableBlockId: string, rowId: string): boolean =>
  blocks.some(b => {
    if (b.type !== 'plan_appendix') return false;
    const plan = b.data as PlanAppendixBlockData;
    return plan.labelSource === 'item'
      && plan.tableBlockId === tableBlockId
      && planMarkersAll(plan).some(m => m.rowId === rowId);
  });

/**
 * The number written in a marker: the linked photo's number, or the linked
 * table row's item number, per the plan's labelSource. Blank when the link is
 * broken (the row or photo was deleted), which the editor shows as an empty
 * pin rather than inventing a number.
 */
export const markerLabel = (
  marker: PlanMarker,
  plan: PlanAppendixBlockData,
  blocks: Block[],
  numbers: Map<string, number>
): string => {
  if (plan.labelSource === 'item') {
    const table = blocks.find(b => b.id === plan.tableBlockId && b.type === 'table_appendix');
    if (!table || !marker.rowId) return '';
    const rows = (table.data as TableAppendixBlockData).rows;
    const index = rows.findIndex(r => r.id === marker.rowId);
    const n = index >= 0 ? itemNumbers(rows)[index] : null;
    return n == null ? '' : String(n);
  }
  if (!marker.imageId) return '';
  const n = numbers.get(marker.imageId);
  return n == null ? '' : String(n);
};

/**
 * What can still be placed on a plan: every candidate (photo or table row,
 * per labelSource) with whether a marker already refers to it.
 */
export interface PlanCandidate {
  key: string;
  label: string;
  rowId?: string;
  imageId?: string;
  placed: boolean;
}

export const planCandidates = (
  plan: PlanAppendixBlockData,
  blocks: Block[],
  numbers: Map<string, number>
): PlanCandidate[] => {
  if (plan.labelSource === 'item') {
    const table = blocks.find(b => b.id === plan.tableBlockId && b.type === 'table_appendix');
    if (!table) return [];
    const rows = (table.data as TableAppendixBlockData).rows;
    const nums = itemNumbers(rows);
    // Placed anywhere in the appendix: the pool is shared across its pages.
    const placed = new Set(planMarkersAll(plan).map(m => m.rowId));
    return rows
      .map((row, i) => ({ row, n: nums[i] }))
      .filter((x): x is { row: typeof x.row; n: number } => x.n != null)
      .map(({ row, n }) => ({
        key: row.id,
        label: `Item ${n}`,
        rowId: row.id,
        placed: placed.has(row.id),
      }));
  }
  const placed = new Set(planMarkersAll(plan).map(m => m.imageId));
  return Array.from(numbers.entries())
    .sort((a, b) => a[1] - b[1])
    .map(([imageId, n]) => ({
      key: imageId,
      label: `Photo ${n}`,
      imageId,
      placed: placed.has(imageId),
    }));
};
